import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * "The AcquizGroup Ecosystem" — an infinite left-scrolling carousel of role
 * cards. Six roles are shown (Seller, Buyer, Consultant, Lender, Broker,
 * Closing Expert); the set is duplicated so the marquee loops seamlessly.
 *
 * Cards keep the same proportions as the previous static 3-column layout
 * (p-8, centered title, description block, full-width CTA). The track pauses
 * on hover and is disabled under prefers-reduced-motion.
 */

type RoleAccent = "accent" | "primary";

type EcoCard = {
  titleKey: string;
  /** single-paragraph descriptions use the _desc key; multi-paragraph use _desc_1..N */
  multi?: boolean;
  descKey: string;
  ctaKey: string;
  to: string;
  accent: RoleAccent;
};

const CARDS: EcoCard[] = [
  {
    titleKey: "index.ecosystem.seller",
    descKey: "index.ecosystem.seller_desc",
    ctaKey: "index.ecosystem.sell_cta",
    to: "/landing/sellers",
    accent: "accent",
  },
  {
    titleKey: "index.ecosystem.buyer",
    multi: true,
    descKey: "index.ecosystem.buyer_desc",
    ctaKey: "index.ecosystem.buy_cta",
    to: "/landing/buyers",
    accent: "primary",
  },
  {
    titleKey: "index.ecosystem.consultant",
    multi: true,
    descKey: "index.ecosystem.consultant_desc",
    ctaKey: "index.ecosystem.consultant_cta",
    to: "/landing/consultants",
    accent: "accent",
  },
  {
    titleKey: "index.ecosystem.lender",
    descKey: "index.ecosystem.lender_desc",
    ctaKey: "index.ecosystem.lender_cta",
    to: "/landing/lenders",
    accent: "primary",
  },
  {
    titleKey: "index.ecosystem.broker",
    descKey: "index.ecosystem.broker_desc",
    ctaKey: "index.ecosystem.broker_cta",
    to: "/landing/brokers",
    accent: "accent",
  },
  {
    titleKey: "index.ecosystem.closing_expert",
    descKey: "index.ecosystem.closing_expert_desc",
    ctaKey: "index.ecosystem.closing_expert_cta",
    to: "/landing/partners",
    accent: "primary",
  },
];

/**
 * Renders a translated string while honoring inline emphasis markers.
 * Segments wrapped in `**...**` are emphasized in the accent color and bold,
 * matching the brand palette (gold/accent) so the key phrases pop.
 */
function renderEmphasis(text: string) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <span
          key={i}
          className="font-semibold text-accent"
        >
          {part.slice(2, -2)}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export function EcosystemCarousel() {
  const { t } = useLanguage();
  const items = [...CARDS, ...CARDS]; // duplicate for seamless loop

  return (
    <div className="relative group">
      {/* edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 sm:w-24 bg-gradient-to-r from-background to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 sm:w-24 bg-gradient-to-l from-background to-transparent" />

      <div className="overflow-hidden">
        <div className="flex w-max gap-8 animate-ecosystem-marquee motion-reduce:animate-none group-hover:[animation-play-state:paused]">
          {items.map((card, i) => {
            const accentClass =
              card.accent === "accent" ? "text-accent" : "text-primary";
            const ctaVariant = card.accent === "accent" ? "accent" : "default";
            return (
              <div
                key={`${card.titleKey}-${i}`}
                className="w-[300px] sm:w-[380px] shrink-0"
              >
                <Card className="p-8 text-center hover:shadow-lg transition-shadow flex flex-col h-full border-border">
                  <CardHeader>
                    <CardTitle className={`text-2xl mb-4 ${accentClass}`}>
                      {t(card.titleKey)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 flex-grow flex flex-col justify-between">
                    {card.multi ? (
                      <div className="text-base text-card-foreground space-y-4">
                        {[1, 2, 3, 4].map((n) => {
                          const key = `${card.descKey}_${n}`;
                          const text = t(key);
                          // Only render paragraphs that resolved (avoid printing
                          // the raw key for roles with fewer than 4 paragraphs)
                          if (!text || text === key) return null;
                          return <p key={n}>{renderEmphasis(text)}</p>;
                        })}
                      </div>
                    ) : (
                      <CardDescription className="text-base text-card-foreground">
                        {renderEmphasis(t(card.descKey))}
                      </CardDescription>
                    )}
                    <Link to={card.to} className="w-full">
                      <Button
                        variant={ctaVariant}
                        className="w-full min-h-[60px]"
                      >
                        {t(card.ctaKey)}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
